/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v3";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { ClosedEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  CodeInterpreterToolDefinition,
  CodeInterpreterToolDefinition$inboundSchema,
  CodeInterpreterToolDefinition$Outbound,
  CodeInterpreterToolDefinition$outboundSchema,
} from "./codeinterpretertooldefinition.js";
import {
  CompactionContextManagement,
  CompactionContextManagement$inboundSchema,
  CompactionContextManagement$Outbound,
  CompactionContextManagement$outboundSchema,
} from "./compactioncontextmanagement.js";
import {
  ComputerUseToolDefinition,
  ComputerUseToolDefinition$inboundSchema,
  ComputerUseToolDefinition$Outbound,
  ComputerUseToolDefinition$outboundSchema,
} from "./computerusetooldefinition.js";
import {
  Conversation,
  Conversation$inboundSchema,
  Conversation$Outbound,
  Conversation$outboundSchema,
} from "./conversation.js";
import {
  CustomToolDefinition,
  CustomToolDefinition$inboundSchema,
  CustomToolDefinition$Outbound,
  CustomToolDefinition$outboundSchema,
} from "./customtooldefinition.js";
import {
  FileSearchToolDefinition,
  FileSearchToolDefinition$inboundSchema,
  FileSearchToolDefinition$Outbound,
  FileSearchToolDefinition$outboundSchema,
} from "./filesearchtooldefinition.js";
import {
  FunctionToolDefinition,
  FunctionToolDefinition$inboundSchema,
  FunctionToolDefinition$Outbound,
  FunctionToolDefinition$outboundSchema,
} from "./functiontooldefinition.js";
import {
  HistoryToolDefinition,
  HistoryToolDefinition$inboundSchema,
  HistoryToolDefinition$Outbound,
  HistoryToolDefinition$outboundSchema,
} from "./historytooldefinition.js";
import {
  HostedToolDefinition,
  HostedToolDefinition$inboundSchema,
  HostedToolDefinition$Outbound,
  HostedToolDefinition$outboundSchema,
} from "./hostedtooldefinition.js";
import {
  ImageGenerationToolDefinition,
  ImageGenerationToolDefinition$inboundSchema,
  ImageGenerationToolDefinition$Outbound,
  ImageGenerationToolDefinition$outboundSchema,
} from "./imagegenerationtooldefinition.js";
import {
  Item,
  Item$inboundSchema,
  Item$Outbound,
  Item$outboundSchema,
} from "./item.js";
import {
  LocalShellToolDefinition,
  LocalShellToolDefinition$inboundSchema,
  LocalShellToolDefinition$Outbound,
  LocalShellToolDefinition$outboundSchema,
} from "./localshelltooldefinition.js";
import {
  MCPToolDefinition,
  MCPToolDefinition$inboundSchema,
  MCPToolDefinition$Outbound,
  MCPToolDefinition$outboundSchema,
} from "./mcptooldefinition.js";
import {
  PromptTemplate,
  PromptTemplate$inboundSchema,
  PromptTemplate$Outbound,
  PromptTemplate$outboundSchema,
} from "./prompttemplate.js";
import {
  ReasoningConfiguration,
  ReasoningConfiguration$inboundSchema,
  ReasoningConfiguration$Outbound,
  ReasoningConfiguration$outboundSchema,
} from "./reasoningconfiguration.js";
import {
  ShellToolDefinition,
  ShellToolDefinition$inboundSchema,
  ShellToolDefinition$Outbound,
  ShellToolDefinition$outboundSchema,
} from "./shelltooldefinition.js";
import {
  SleepToolDefinition,
  SleepToolDefinition$inboundSchema,
  SleepToolDefinition$Outbound,
  SleepToolDefinition$outboundSchema,
} from "./sleeptooldefinition.js";
import {
  TextConfiguration,
  TextConfiguration$inboundSchema,
  TextConfiguration$Outbound,
  TextConfiguration$outboundSchema,
} from "./textconfiguration.js";
import {
  ToolChoice,
  ToolChoice$inboundSchema,
  ToolChoice$Outbound,
  ToolChoice$outboundSchema,
} from "./toolchoice.js";
import {
  WebSearchPreviewToolDefinition,
  WebSearchPreviewToolDefinition$inboundSchema,
  WebSearchPreviewToolDefinition$Outbound,
  WebSearchPreviewToolDefinition$outboundSchema,
} from "./websearchpreviewtooldefinition.js";
import {
  WebSearchToolDefinition,
  WebSearchToolDefinition$inboundSchema,
  WebSearchToolDefinition$Outbound,
  WebSearchToolDefinition$outboundSchema,
} from "./websearchtooldefinition.js";

/**
 * The user input to send to the agent. Can be a simple text string for basic queries, or an array of item objects containing text, images, files, or audio for multimodal interactions. This is the main content the agent will respond to.
 */
export type ResponsesCreateRequestInput = string | Array<Item>;

export type ResponsesCreateRequestConversation = Conversation | string;

export const Include = {
  WebSearchCallActionSources: "web_search_call.action.sources",
  CodeInterpreterCallOutputs: "code_interpreter_call.outputs",
  ComputerCallOutputOutputImageUrl: "computer_call_output.output.image_url",
  FileSearchCallResults: "file_search_call.results",
  MessageInputImageImageUrl: "message.input_image.image_url",
  MessageOutputTextLogprobs: "message.output_text.logprobs",
  ReasoningEncryptedContent: "reasoning.encrypted_content",
} as const;
export type Include = ClosedEnum<typeof Include>;

export type ResponsesCreateRequestStreamOptions = {
  /**
   * Whether to include token usage statistics in the stream. Useful for monitoring costs in real-time.
   */
  includeUsage?: boolean | undefined;
  /**
   * Whether to enable stream obfuscation for privacy and security purposes during streaming.
   */
  includeObfuscation?: boolean | undefined;
};

export type Tools =
  | FunctionToolDefinition
  | ComputerUseToolDefinition
  | FileSearchToolDefinition
  | CodeInterpreterToolDefinition
  | CustomToolDefinition
  | WebSearchToolDefinition
  | MCPToolDefinition
  | ImageGenerationToolDefinition
  | LocalShellToolDefinition
  | ShellToolDefinition
  | WebSearchPreviewToolDefinition
  | HostedToolDefinition
  | SleepToolDefinition
  | HistoryToolDefinition;

/**
 * Gateway extension. Stop sequence(s) where compatible upstream providers should stop generation. Eval runs also enforce this as a post-processing guard.
 */
export type ResponsesCreateRequestStop = string | Array<string>;

/**
 * Gateway extension. Provider-specific thinking prefill toggle or prefill text.
 */
export type ResponsesCreateRequestPrefillThink = boolean | string;

/**
 * The truncation strategy for handling inputs that exceed the model's context window. 'auto' automatically truncates old messages to fit, 'disabled' returns an error if context is too long. Defaults to 'auto'.
 */
export const ResponsesCreateRequestTruncation = {
  Auto: "auto",
  Disabled: "disabled",
} as const;
/**
 * The truncation strategy for handling inputs that exceed the model's context window. 'auto' automatically truncates old messages to fit, 'disabled' returns an error if context is too long. Defaults to 'auto'.
 */
export type ResponsesCreateRequestTruncation = ClosedEnum<
  typeof ResponsesCreateRequestTruncation
>;

/**
 * Specifies the processing tier for serving the request. 'auto' lets the system choose, 'default' uses standard processing, 'flex' allows flexible scheduling for lower priority, 'priority' provides faster processing. Defaults to 'auto'.
 */
export const ResponsesCreateRequestServiceTier = {
  Auto: "auto",
  Default: "default",
  Flex: "flex",
  Priority: "priority",
} as const;
/**
 * Specifies the processing tier for serving the request. 'auto' lets the system choose, 'default' uses standard processing, 'flex' allows flexible scheduling for lower priority, 'priority' provides faster processing. Defaults to 'auto'.
 */
export type ResponsesCreateRequestServiceTier = ClosedEnum<
  typeof ResponsesCreateRequestServiceTier
>;

export const Modalities = {
  Text: "text",
  Audio: "audio",
} as const;
export type Modalities = ClosedEnum<typeof Modalities>;

/**
 * The audio format for the output. Defaults to 'wav'.
 */
export const ResponsesCreateRequestFormat = {
  Wav: "wav",
  Mp3: "mp3",
  Flac: "flac",
  Opus: "opus",
  Pcm16: "pcm16",
} as const;
/**
 * The audio format for the output. Defaults to 'wav'.
 */
export type ResponsesCreateRequestFormat = ClosedEnum<
  typeof ResponsesCreateRequestFormat
>;

/**
 * Audio output configuration. Optional when modalities includes 'audio'; defaults are applied if omitted.
 */
export type Audio = {
  /**
   * The voice to use for audio output synthesis. Defaults to 'alloy'.
   */
  voice?: string | undefined;
  /**
   * The audio format for the output. Defaults to 'wav'.
   */
  format?: ResponsesCreateRequestFormat | undefined;
};

/**
 * Request schema for creating a new agent response. Configures the agent's model, input, tools, output format, and behavior. Supports both foreground (blocking) and background (asynchronous) execution with optional streaming.
 */
export type ResponsesCreateRequest = {
  /**
   * The ID of the model to use for generating the response.
   */
  model: string;
  /**
   * The user input to send to the agent. Can be a simple text string for basic queries, or an array of item objects containing text, images, files, or audio for multimodal interactions. This is the main content the agent will respond to.
   */
  input?: string | Array<Item> | undefined;
  /**
   * System or developer instructions inserted into the model's context before user input. Use this to guide the agent's behavior, set personality, define constraints, or provide domain-specific knowledge. Acts as a persistent system message for this response.
   */
  instructions?: string | undefined;
  /**
   * The conversation that this response belongs to. Can be a conversation ID string or a conversation object. Used to maintain context and history across multiple agent interactions. Optional - omit for one-off interactions.
   */
  conversation?: Conversation | string | null | undefined;
  /**
   * The unique ID of a previous response to continue from. Used for multi-turn conversations to maintain context and history. The agent will have access to all previous interactions in the chain.
   */
  previousResponseId?: string | undefined;
  prompt?: PromptTemplate | undefined;
  /**
   * Additional fields to include in the response output. Allows requesting specific nested data like web search sources, code interpreter outputs, computer screenshots, file search results, input images, output logprobs, or reasoning content. These fields may have performance or cost implications.
   */
  include?: Array<Include> | undefined;
  text?: TextConfiguration | undefined;
  /**
   * If set to true, the response data will be streamed using Server-Sent Events (SSE) for real-time updates as the agent generates the response. When false, the response is returned as a single complete object. Defaults to false.
   */
  stream?: boolean | undefined;
  /**
   * Additional options for configuring streaming behavior when stream is enabled.
   */
  streamOptions?: ResponsesCreateRequestStreamOptions | null | undefined;
  /**
   * Whether to store the generated response for later retrieval. When true, the response is saved and can be retrieved via GET /responses/{id}. When false, the response is not persisted after generation. Defaults to true.
   */
  store?: boolean | undefined;
  /**
   * Whether to run the model response in the background asynchronously. When true, the request returns immediately with a response ID while the agent processes in the background. Use GET /responses/{id} to retrieve results later. When false, the request blocks until completion. Defaults to false.
   */
  background?: boolean | undefined;
  /**
   * URL to receive webhook notifications for status changes (queued, in_progress, completed, failed, incomplete). Only valid when background=true. If provided without background=true, the request will be rejected.
   */
  webhookUrl?: string | undefined;
  /**
   * Optional secret for HMAC-SHA256 signing of webhook payloads. When provided, webhooks include an X-Webhook-Signature header with the signature. Requires webhook_url to be set.
   */
  webhookSecret?: string | undefined;
  /**
   * Array of tool definitions that the agent can use during response generation. Supports built-in tools (web search, file search, code interpreter, computer use, image generation) and custom tools (functions, MCP, local shell). Each tool definition specifies its type and configuration.
   */
  tools?:
    | Array<
      | FunctionToolDefinition
      | ComputerUseToolDefinition
      | FileSearchToolDefinition
      | CodeInterpreterToolDefinition
      | CustomToolDefinition
      | WebSearchToolDefinition
      | MCPToolDefinition
      | ImageGenerationToolDefinition
      | LocalShellToolDefinition
      | ShellToolDefinition
      | WebSearchPreviewToolDefinition
      | HostedToolDefinition
      | SleepToolDefinition
      | HistoryToolDefinition
    >
    | undefined;
  toolChoice?: ToolChoice | undefined;
  /**
   * Whether to allow the agent to execute multiple tool calls in parallel. When true, the agent can make concurrent tool calls for efficiency. When false, tools are called sequentially. Defaults to true for better performance.
   */
  parallelToolCalls?: boolean | undefined;
  /**
   * The maximum total number of tool calls the agent can make during this response. Useful for controlling execution time and preventing infinite loops. Applies to all built-in tools like web search, file search, code interpreter, etc. Must be a positive integer between 1 and 300. Defaults to 64.
   */
  maxToolCalls?: number | undefined;
  reasoning?: ReasoningConfiguration | undefined;
  /**
   * When true, the gateway inspects request complexity and automatically routes between quantized, MoE, and dense variants within the requested model family.
   */
  autoRouting?: boolean | undefined;
  /**
   * Gateway extension. When true alongside auto_routing, the response metadata includes a compact serialized routing decision so you can inspect the inferred target tier even when no compatible sibling variant is available.
   */
  autoRoutingDebug?: boolean | undefined;
  /**
   * The maximum number of tokens the model can generate in its output. Sets an upper bound to control costs and response length. The actual output may be shorter if the model finishes naturally. Must be a positive integer.
   */
  maxOutputTokens?: number | undefined;
  /**
   * Controls randomness in the model's output. Higher values (e.g., 1.5-2.0) make output more random and creative, lower values (e.g., 0.0-0.5) make it more focused and deterministic. Must be between 0 and 2.
   */
  temperature?: number | undefined;
  /**
   * Nucleus sampling parameter. The model considers only the tokens with top_p cumulative probability. Lower values (e.g., 0.1) make output more focused, higher values (e.g., 0.9) allow more diversity. Must be between 0 and 1. Alternative to temperature.
   */
  topP?: number | undefined;
  /**
   * Gateway extension. Stop sequence(s) where compatible upstream providers should stop generation. Eval runs also enforce this as a post-processing guard.
   */
  stop?: string | Array<string> | undefined;
  /**
   * Gateway extension. Provider-specific top-k sampling parameter.
   */
  topK?: number | undefined;
  /**
   * Gateway extension. Provider-specific min-p sampling parameter.
   */
  minP?: number | undefined;
  /**
   * Gateway extension. Provider-specific repetition penalty.
   */
  repetitionPenalty?: number | undefined;
  /**
   * Gateway extension. Provider-specific sampling toggle.
   */
  doSample?: boolean | undefined;
  /**
   * Gateway extension. Controlled provider-specific request body passthrough.
   */
  extraBody?: { [k: string]: any } | undefined;
  /**
   * Gateway extension. Provider-specific chat template options, such as enable_thinking=false.
   */
  chatTemplateKwargs?: { [k: string]: any } | undefined;
  /**
   * Gateway extension. Provider-specific thinking prefill toggle or prefill text.
   */
  prefillThink?: boolean | string | undefined;
  /**
   * Gateway extension. Provider-specific cache toggle where supported.
   */
  useCache?: boolean | undefined;
  /**
   * The number of most likely tokens to return at each position along with their log probabilities. Must be between 0 and 20. Useful for understanding model confidence and exploring alternative outputs.
   */
  topLogprobs?: number | undefined;
  /**
   * The truncation strategy for handling inputs that exceed the model's context window. 'auto' automatically truncates old messages to fit, 'disabled' returns an error if context is too long. Defaults to 'auto'.
   */
  truncation?: ResponsesCreateRequestTruncation | undefined;
  /**
   * Array of context management strategies to apply during response generation.
   */
  contextManagement?: Array<CompactionContextManagement> | undefined;
  /**
   * Penalizes new tokens based on whether they appear in the text so far. Higher values increase the model's likelihood to talk about new topics.
   */
  presencePenalty?: number | undefined;
  /**
   * Penalizes new tokens based on their frequency in the text so far. Higher values decrease the model's likelihood to repeat the same line verbatim.
   */
  frequencyPenalty?: number | undefined;
  /**
   * Specifies the processing tier for serving the request. 'auto' lets the system choose, 'default' uses standard processing, 'flex' allows flexible scheduling for lower priority, 'priority' provides faster processing. Defaults to 'auto'.
   */
  serviceTier?: ResponsesCreateRequestServiceTier | undefined;
  /**
   * A stable identifier used for caching prompts and context. Helps reduce costs and latency by reusing cached prompt processing. Useful for prompts that are used repeatedly with minor variations.
   */
  promptCacheKey?: string | undefined;
  /**
   * A stable identifier used to help detect and prevent policy violations across multiple requests. Helps the system identify patterns of abuse or misuse. Useful for compliance tracking and safety monitoring.
   */
  safetyIdentifier?: string | undefined;
  /**
   * Custom metadata to attach to this response as key-value pairs. Keys must be strings up to 64 characters, values must be strings up to 512 characters. Maximum 16 pairs. Useful for tracking, categorization, or passing custom identifiers through your system.
   */
  metadata?: { [k: string]: string } | undefined;
  /**
   * Output types that the model should generate. Defaults to ['text']. Include 'audio' to receive an audio rendering of the text response via TTS.
   */
  modalities?: Array<Modalities> | undefined;
  /**
   * Audio output configuration. Optional when modalities includes 'audio'; defaults are applied if omitted.
   */
  audio?: Audio | undefined;
  /**
   * DEPRECATED: A unique identifier for the end-user. Use safety_identifier for policy violation detection and prompt_cache_key for caching instead. This field is maintained for backwards compatibility.
   */
  user?: string | undefined;
};

/** @internal */
export const ResponsesCreateRequestInput$inboundSchema: z.ZodType<
  ResponsesCreateRequestInput,
  z.ZodTypeDef,
  unknown
> = z.union([z.string(), z.array(Item$inboundSchema)]);
/** @internal */
export type ResponsesCreateRequestInput$Outbound =
  | string
  | Array<Item$Outbound>;

/** @internal */
export const ResponsesCreateRequestInput$outboundSchema: z.ZodType<
  ResponsesCreateRequestInput$Outbound,
  z.ZodTypeDef,
  ResponsesCreateRequestInput
> = z.union([z.string(), z.array(Item$outboundSchema)]);

export function responsesCreateRequestInputToJSON(
  responsesCreateRequestInput: ResponsesCreateRequestInput,
): string {
  return JSON.stringify(
    ResponsesCreateRequestInput$outboundSchema.parse(
      responsesCreateRequestInput,
    ),
  );
}
export function responsesCreateRequestInputFromJSON(
  jsonString: string,
): SafeParseResult<ResponsesCreateRequestInput, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => ResponsesCreateRequestInput$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'ResponsesCreateRequestInput' from JSON`,
  );
}

/** @internal */
export const ResponsesCreateRequestConversation$inboundSchema: z.ZodType<
  ResponsesCreateRequestConversation,
  z.ZodTypeDef,
  unknown
> = z.union([Conversation$inboundSchema, z.string()]);
/** @internal */
export type ResponsesCreateRequestConversation$Outbound =
  | Conversation$Outbound
  | string;

/** @internal */
export const ResponsesCreateRequestConversation$outboundSchema: z.ZodType<
  ResponsesCreateRequestConversation$Outbound,
  z.ZodTypeDef,
  ResponsesCreateRequestConversation
> = z.union([Conversation$outboundSchema, z.string()]);

export function responsesCreateRequestConversationToJSON(
  responsesCreateRequestConversation: ResponsesCreateRequestConversation,
): string {
  return JSON.stringify(
    ResponsesCreateRequestConversation$outboundSchema.parse(
      responsesCreateRequestConversation,
    ),
  );
}
export function responsesCreateRequestConversationFromJSON(
  jsonString: string,
): SafeParseResult<ResponsesCreateRequestConversation, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) =>
      ResponsesCreateRequestConversation$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'ResponsesCreateRequestConversation' from JSON`,
  );
}

/** @internal */
export const Include$inboundSchema: z.ZodNativeEnum<typeof Include> = z
  .nativeEnum(Include);
/** @internal */
export const Include$outboundSchema: z.ZodNativeEnum<typeof Include> =
  Include$inboundSchema;

/** @internal */
export const ResponsesCreateRequestStreamOptions$inboundSchema: z.ZodType<
  ResponsesCreateRequestStreamOptions,
  z.ZodTypeDef,
  unknown
> = z.object({
  include_usage: z.boolean().optional(),
  include_obfuscation: z.boolean().optional(),
}).transform((v) => {
  return remap$(v, {
    "include_usage": "includeUsage",
    "include_obfuscation": "includeObfuscation",
  });
});
/** @internal */
export type ResponsesCreateRequestStreamOptions$Outbound = {
  include_usage?: boolean | undefined;
  include_obfuscation?: boolean | undefined;
};

/** @internal */
export const ResponsesCreateRequestStreamOptions$outboundSchema: z.ZodType<
  ResponsesCreateRequestStreamOptions$Outbound,
  z.ZodTypeDef,
  ResponsesCreateRequestStreamOptions
> = z.object({
  includeUsage: z.boolean().optional(),
  includeObfuscation: z.boolean().optional(),
}).transform((v) => {
  return remap$(v, {
    includeUsage: "include_usage",
    includeObfuscation: "include_obfuscation",
  });
});

export function responsesCreateRequestStreamOptionsToJSON(
  responsesCreateRequestStreamOptions: ResponsesCreateRequestStreamOptions,
): string {
  return JSON.stringify(
    ResponsesCreateRequestStreamOptions$outboundSchema.parse(
      responsesCreateRequestStreamOptions,
    ),
  );
}
export function responsesCreateRequestStreamOptionsFromJSON(
  jsonString: string,
): SafeParseResult<ResponsesCreateRequestStreamOptions, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) =>
      ResponsesCreateRequestStreamOptions$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'ResponsesCreateRequestStreamOptions' from JSON`,
  );
}

/** @internal */
export const Tools$inboundSchema: z.ZodType<Tools, z.ZodTypeDef, unknown> = z
  .union([
    FunctionToolDefinition$inboundSchema,
    ComputerUseToolDefinition$inboundSchema,
    FileSearchToolDefinition$inboundSchema,
    CodeInterpreterToolDefinition$inboundSchema,
    CustomToolDefinition$inboundSchema,
    WebSearchToolDefinition$inboundSchema,
    MCPToolDefinition$inboundSchema,
    ImageGenerationToolDefinition$inboundSchema,
    LocalShellToolDefinition$inboundSchema,
    ShellToolDefinition$inboundSchema,
    WebSearchPreviewToolDefinition$inboundSchema,
    HostedToolDefinition$inboundSchema,
    SleepToolDefinition$inboundSchema,
    HistoryToolDefinition$inboundSchema,
  ]);
/** @internal */
export type Tools$Outbound =
  | FunctionToolDefinition$Outbound
  | ComputerUseToolDefinition$Outbound
  | FileSearchToolDefinition$Outbound
  | CodeInterpreterToolDefinition$Outbound
  | CustomToolDefinition$Outbound
  | WebSearchToolDefinition$Outbound
  | MCPToolDefinition$Outbound
  | ImageGenerationToolDefinition$Outbound
  | LocalShellToolDefinition$Outbound
  | ShellToolDefinition$Outbound
  | WebSearchPreviewToolDefinition$Outbound
  | HostedToolDefinition$Outbound
  | SleepToolDefinition$Outbound
  | HistoryToolDefinition$Outbound;

/** @internal */
export const Tools$outboundSchema: z.ZodType<
  Tools$Outbound,
  z.ZodTypeDef,
  Tools
> = z.union([
  FunctionToolDefinition$outboundSchema,
  ComputerUseToolDefinition$outboundSchema,
  FileSearchToolDefinition$outboundSchema,
  CodeInterpreterToolDefinition$outboundSchema,
  CustomToolDefinition$outboundSchema,
  WebSearchToolDefinition$outboundSchema,
  MCPToolDefinition$outboundSchema,
  ImageGenerationToolDefinition$outboundSchema,
  LocalShellToolDefinition$outboundSchema,
  ShellToolDefinition$outboundSchema,
  WebSearchPreviewToolDefinition$outboundSchema,
  HostedToolDefinition$outboundSchema,
  SleepToolDefinition$outboundSchema,
  HistoryToolDefinition$outboundSchema,
]);

export function toolsToJSON(tools: Tools): string {
  return JSON.stringify(Tools$outboundSchema.parse(tools));
}
export function toolsFromJSON(
  jsonString: string,
): SafeParseResult<Tools, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => Tools$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'Tools' from JSON`,
  );
}

/** @internal */
export const ResponsesCreateRequestStop$inboundSchema: z.ZodType<
  ResponsesCreateRequestStop,
  z.ZodTypeDef,
  unknown
> = z.union([z.string(), z.array(z.string())]);
/** @internal */
export type ResponsesCreateRequestStop$Outbound = string | Array<string>;

/** @internal */
export const ResponsesCreateRequestStop$outboundSchema: z.ZodType<
  ResponsesCreateRequestStop$Outbound,
  z.ZodTypeDef,
  ResponsesCreateRequestStop
> = z.union([z.string(), z.array(z.string())]);

export function responsesCreateRequestStopToJSON(
  responsesCreateRequestStop: ResponsesCreateRequestStop,
): string {
  return JSON.stringify(
    ResponsesCreateRequestStop$outboundSchema.parse(responsesCreateRequestStop),
  );
}
export function responsesCreateRequestStopFromJSON(
  jsonString: string,
): SafeParseResult<ResponsesCreateRequestStop, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => ResponsesCreateRequestStop$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'ResponsesCreateRequestStop' from JSON`,
  );
}

/** @internal */
export const ResponsesCreateRequestPrefillThink$inboundSchema: z.ZodType<
  ResponsesCreateRequestPrefillThink,
  z.ZodTypeDef,
  unknown
> = z.union([z.boolean(), z.string()]);
/** @internal */
export type ResponsesCreateRequestPrefillThink$Outbound = boolean | string;

/** @internal */
export const ResponsesCreateRequestPrefillThink$outboundSchema: z.ZodType<
  ResponsesCreateRequestPrefillThink$Outbound,
  z.ZodTypeDef,
  ResponsesCreateRequestPrefillThink
> = z.union([z.boolean(), z.string()]);

export function responsesCreateRequestPrefillThinkToJSON(
  responsesCreateRequestPrefillThink: ResponsesCreateRequestPrefillThink,
): string {
  return JSON.stringify(
    ResponsesCreateRequestPrefillThink$outboundSchema.parse(
      responsesCreateRequestPrefillThink,
    ),
  );
}
export function responsesCreateRequestPrefillThinkFromJSON(
  jsonString: string,
): SafeParseResult<ResponsesCreateRequestPrefillThink, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) =>
      ResponsesCreateRequestPrefillThink$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'ResponsesCreateRequestPrefillThink' from JSON`,
  );
}

/** @internal */
export const ResponsesCreateRequestTruncation$inboundSchema: z.ZodNativeEnum<
  typeof ResponsesCreateRequestTruncation
> = z.nativeEnum(ResponsesCreateRequestTruncation);
/** @internal */
export const ResponsesCreateRequestTruncation$outboundSchema: z.ZodNativeEnum<
  typeof ResponsesCreateRequestTruncation
> = ResponsesCreateRequestTruncation$inboundSchema;

/** @internal */
export const ResponsesCreateRequestServiceTier$inboundSchema: z.ZodNativeEnum<
  typeof ResponsesCreateRequestServiceTier
> = z.nativeEnum(ResponsesCreateRequestServiceTier);
/** @internal */
export const ResponsesCreateRequestServiceTier$outboundSchema: z.ZodNativeEnum<
  typeof ResponsesCreateRequestServiceTier
> = ResponsesCreateRequestServiceTier$inboundSchema;

/** @internal */
export const Modalities$inboundSchema: z.ZodNativeEnum<typeof Modalities> = z
  .nativeEnum(Modalities);
/** @internal */
export const Modalities$outboundSchema: z.ZodNativeEnum<typeof Modalities> =
  Modalities$inboundSchema;

/** @internal */
export const ResponsesCreateRequestFormat$inboundSchema: z.ZodNativeEnum<
  typeof ResponsesCreateRequestFormat
> = z.nativeEnum(ResponsesCreateRequestFormat);
/** @internal */
export const ResponsesCreateRequestFormat$outboundSchema: z.ZodNativeEnum<
  typeof ResponsesCreateRequestFormat
> = ResponsesCreateRequestFormat$inboundSchema;

/** @internal */
export const Audio$inboundSchema: z.ZodType<Audio, z.ZodTypeDef, unknown> = z
  .object({
    voice: z.string().default("alloy"),
    format: ResponsesCreateRequestFormat$inboundSchema.default("wav"),
  });
/** @internal */
export type Audio$Outbound = {
  voice: string;
  format: string;
};

/** @internal */
export const Audio$outboundSchema: z.ZodType<
  Audio$Outbound,
  z.ZodTypeDef,
  Audio
> = z.object({
  voice: z.string().default("alloy"),
  format: ResponsesCreateRequestFormat$outboundSchema.default("wav"),
});

export function audioToJSON(audio: Audio): string {
  return JSON.stringify(Audio$outboundSchema.parse(audio));
}
export function audioFromJSON(
  jsonString: string,
): SafeParseResult<Audio, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => Audio$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'Audio' from JSON`,
  );
}

/** @internal */
export const ResponsesCreateRequest$inboundSchema: z.ZodType<
  ResponsesCreateRequest,
  z.ZodTypeDef,
  unknown
> = z.object({
  model: z.string(),
  input: z.union([z.string(), z.array(Item$inboundSchema)]).optional(),
  instructions: z.string().optional(),
  conversation: z.nullable(z.union([Conversation$inboundSchema, z.string()]))
    .optional(),
  previous_response_id: z.string().optional(),
  prompt: PromptTemplate$inboundSchema.optional(),
  include: z.array(Include$inboundSchema).optional(),
  text: TextConfiguration$inboundSchema.optional(),
  stream: z.boolean().default(false),
  stream_options: z.nullable(
    z.lazy(() => ResponsesCreateRequestStreamOptions$inboundSchema),
  ).optional(),
  store: z.boolean().default(true),
  background: z.boolean().default(false),
  webhook_url: z.string().optional(),
  webhook_secret: z.string().optional(),
  tools: z.array(
    z.union([
      FunctionToolDefinition$inboundSchema,
      ComputerUseToolDefinition$inboundSchema,
      FileSearchToolDefinition$inboundSchema,
      CodeInterpreterToolDefinition$inboundSchema,
      CustomToolDefinition$inboundSchema,
      WebSearchToolDefinition$inboundSchema,
      MCPToolDefinition$inboundSchema,
      ImageGenerationToolDefinition$inboundSchema,
      LocalShellToolDefinition$inboundSchema,
      ShellToolDefinition$inboundSchema,
      WebSearchPreviewToolDefinition$inboundSchema,
      HostedToolDefinition$inboundSchema,
      SleepToolDefinition$inboundSchema,
      HistoryToolDefinition$inboundSchema,
    ]),
  ).optional(),
  tool_choice: ToolChoice$inboundSchema.optional(),
  parallel_tool_calls: z.boolean().default(true),
  max_tool_calls: z.number().int().default(64),
  reasoning: ReasoningConfiguration$inboundSchema.optional(),
  auto_routing: z.boolean().optional(),
  auto_routing_debug: z.boolean().optional(),
  max_output_tokens: z.number().int().optional(),
  temperature: z.number().optional(),
  top_p: z.number().optional(),
  stop: z.union([z.string(), z.array(z.string())]).optional(),
  top_k: z.number().int().optional(),
  min_p: z.number().optional(),
  repetition_penalty: z.number().optional(),
  do_sample: z.boolean().optional(),
  extra_body: z.record(z.any()).optional(),
  chat_template_kwargs: z.record(z.any()).optional(),
  prefill_think: z.union([z.boolean(), z.string()]).optional(),
  use_cache: z.boolean().optional(),
  top_logprobs: z.number().int().optional(),
  truncation: ResponsesCreateRequestTruncation$inboundSchema.default("auto"),
  context_management: z.array(CompactionContextManagement$inboundSchema)
    .optional(),
  presence_penalty: z.number().optional(),
  frequency_penalty: z.number().optional(),
  service_tier: ResponsesCreateRequestServiceTier$inboundSchema.default("auto"),
  prompt_cache_key: z.string().optional(),
  safety_identifier: z.string().optional(),
  metadata: z.record(z.string()).optional(),
  modalities: z.array(Modalities$inboundSchema).optional(),
  audio: z.lazy(() => Audio$inboundSchema).optional(),
  user: z.string().optional(),
}).transform((v) => {
  return remap$(v, {
    "previous_response_id": "previousResponseId",
    "stream_options": "streamOptions",
    "webhook_url": "webhookUrl",
    "webhook_secret": "webhookSecret",
    "tool_choice": "toolChoice",
    "parallel_tool_calls": "parallelToolCalls",
    "max_tool_calls": "maxToolCalls",
    "auto_routing": "autoRouting",
    "auto_routing_debug": "autoRoutingDebug",
    "max_output_tokens": "maxOutputTokens",
    "top_p": "topP",
    "top_k": "topK",
    "min_p": "minP",
    "repetition_penalty": "repetitionPenalty",
    "do_sample": "doSample",
    "extra_body": "extraBody",
    "chat_template_kwargs": "chatTemplateKwargs",
    "prefill_think": "prefillThink",
    "use_cache": "useCache",
    "top_logprobs": "topLogprobs",
    "context_management": "contextManagement",
    "presence_penalty": "presencePenalty",
    "frequency_penalty": "frequencyPenalty",
    "service_tier": "serviceTier",
    "prompt_cache_key": "promptCacheKey",
    "safety_identifier": "safetyIdentifier",
  });
});
/** @internal */
export type ResponsesCreateRequest$Outbound = {
  model: string;
  input?: string | Array<Item$Outbound> | undefined;
  instructions?: string | undefined;
  conversation?: Conversation$Outbound | string | null | undefined;
  previous_response_id?: string | undefined;
  prompt?: PromptTemplate$Outbound | undefined;
  include?: Array<string> | undefined;
  text?: TextConfiguration$Outbound | undefined;
  stream: boolean;
  stream_options?:
    | ResponsesCreateRequestStreamOptions$Outbound
    | null
    | undefined;
  store: boolean;
  background: boolean;
  webhook_url?: string | undefined;
  webhook_secret?: string | undefined;
  tools?:
    | Array<
      | FunctionToolDefinition$Outbound
      | ComputerUseToolDefinition$Outbound
      | FileSearchToolDefinition$Outbound
      | CodeInterpreterToolDefinition$Outbound
      | CustomToolDefinition$Outbound
      | WebSearchToolDefinition$Outbound
      | MCPToolDefinition$Outbound
      | ImageGenerationToolDefinition$Outbound
      | LocalShellToolDefinition$Outbound
      | ShellToolDefinition$Outbound
      | WebSearchPreviewToolDefinition$Outbound
      | HostedToolDefinition$Outbound
      | SleepToolDefinition$Outbound
      | HistoryToolDefinition$Outbound
    >
    | undefined;
  tool_choice?: ToolChoice$Outbound | undefined;
  parallel_tool_calls: boolean;
  max_tool_calls: number;
  reasoning?: ReasoningConfiguration$Outbound | undefined;
  auto_routing?: boolean | undefined;
  auto_routing_debug?: boolean | undefined;
  max_output_tokens?: number | undefined;
  temperature?: number | undefined;
  top_p?: number | undefined;
  stop?: string | Array<string> | undefined;
  top_k?: number | undefined;
  min_p?: number | undefined;
  repetition_penalty?: number | undefined;
  do_sample?: boolean | undefined;
  extra_body?: { [k: string]: any } | undefined;
  chat_template_kwargs?: { [k: string]: any } | undefined;
  prefill_think?: boolean | string | undefined;
  use_cache?: boolean | undefined;
  top_logprobs?: number | undefined;
  truncation: string;
  context_management?: Array<CompactionContextManagement$Outbound> | undefined;
  presence_penalty?: number | undefined;
  frequency_penalty?: number | undefined;
  service_tier: string;
  prompt_cache_key?: string | undefined;
  safety_identifier?: string | undefined;
  metadata?: { [k: string]: string } | undefined;
  modalities?: Array<string> | undefined;
  audio?: Audio$Outbound | undefined;
  user?: string | undefined;
};

/** @internal */
export const ResponsesCreateRequest$outboundSchema: z.ZodType<
  ResponsesCreateRequest$Outbound,
  z.ZodTypeDef,
  ResponsesCreateRequest
> = z.object({
  model: z.string(),
  input: z.union([z.string(), z.array(Item$outboundSchema)]).optional(),
  instructions: z.string().optional(),
  conversation: z.nullable(z.union([Conversation$outboundSchema, z.string()]))
    .optional(),
  previousResponseId: z.string().optional(),
  prompt: PromptTemplate$outboundSchema.optional(),
  include: z.array(Include$outboundSchema).optional(),
  text: TextConfiguration$outboundSchema.optional(),
  stream: z.boolean().default(false),
  streamOptions: z.nullable(
    z.lazy(() => ResponsesCreateRequestStreamOptions$outboundSchema),
  ).optional(),
  store: z.boolean().default(true),
  background: z.boolean().default(false),
  webhookUrl: z.string().optional(),
  webhookSecret: z.string().optional(),
  tools: z.array(
    z.union([
      FunctionToolDefinition$outboundSchema,
      ComputerUseToolDefinition$outboundSchema,
      FileSearchToolDefinition$outboundSchema,
      CodeInterpreterToolDefinition$outboundSchema,
      CustomToolDefinition$outboundSchema,
      WebSearchToolDefinition$outboundSchema,
      MCPToolDefinition$outboundSchema,
      ImageGenerationToolDefinition$outboundSchema,
      LocalShellToolDefinition$outboundSchema,
      ShellToolDefinition$outboundSchema,
      WebSearchPreviewToolDefinition$outboundSchema,
      HostedToolDefinition$outboundSchema,
      SleepToolDefinition$outboundSchema,
      HistoryToolDefinition$outboundSchema,
    ]),
  ).optional(),
  toolChoice: ToolChoice$outboundSchema.optional(),
  parallelToolCalls: z.boolean().default(true),
  maxToolCalls: z.number().int().default(64),
  reasoning: ReasoningConfiguration$outboundSchema.optional(),
  autoRouting: z.boolean().optional(),
  autoRoutingDebug: z.boolean().optional(),
  maxOutputTokens: z.number().int().optional(),
  temperature: z.number().optional(),
  topP: z.number().optional(),
  stop: z.union([z.string(), z.array(z.string())]).optional(),
  topK: z.number().int().optional(),
  minP: z.number().optional(),
  repetitionPenalty: z.number().optional(),
  doSample: z.boolean().optional(),
  extraBody: z.record(z.any()).optional(),
  chatTemplateKwargs: z.record(z.any()).optional(),
  prefillThink: z.union([z.boolean(), z.string()]).optional(),
  useCache: z.boolean().optional(),
  topLogprobs: z.number().int().optional(),
  truncation: ResponsesCreateRequestTruncation$outboundSchema.default("auto"),
  contextManagement: z.array(CompactionContextManagement$outboundSchema)
    .optional(),
  presencePenalty: z.number().optional(),
  frequencyPenalty: z.number().optional(),
  serviceTier: ResponsesCreateRequestServiceTier$outboundSchema.default("auto"),
  promptCacheKey: z.string().optional(),
  safetyIdentifier: z.string().optional(),
  metadata: z.record(z.string()).optional(),
  modalities: z.array(Modalities$outboundSchema).optional(),
  audio: z.lazy(() => Audio$outboundSchema).optional(),
  user: z.string().optional(),
}).transform((v) => {
  return remap$(v, {
    previousResponseId: "previous_response_id",
    streamOptions: "stream_options",
    webhookUrl: "webhook_url",
    webhookSecret: "webhook_secret",
    toolChoice: "tool_choice",
    parallelToolCalls: "parallel_tool_calls",
    maxToolCalls: "max_tool_calls",
    autoRouting: "auto_routing",
    autoRoutingDebug: "auto_routing_debug",
    maxOutputTokens: "max_output_tokens",
    topP: "top_p",
    topK: "top_k",
    minP: "min_p",
    repetitionPenalty: "repetition_penalty",
    doSample: "do_sample",
    extraBody: "extra_body",
    chatTemplateKwargs: "chat_template_kwargs",
    prefillThink: "prefill_think",
    useCache: "use_cache",
    topLogprobs: "top_logprobs",
    contextManagement: "context_management",
    presencePenalty: "presence_penalty",
    frequencyPenalty: "frequency_penalty",
    serviceTier: "service_tier",
    promptCacheKey: "prompt_cache_key",
    safetyIdentifier: "safety_identifier",
  });
});

export function responsesCreateRequestToJSON(
  responsesCreateRequest: ResponsesCreateRequest,
): string {
  return JSON.stringify(
    ResponsesCreateRequest$outboundSchema.parse(responsesCreateRequest),
  );
}
export function responsesCreateRequestFromJSON(
  jsonString: string,
): SafeParseResult<ResponsesCreateRequest, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => ResponsesCreateRequest$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'ResponsesCreateRequest' from JSON`,
  );
}
